import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSEO, getOrganizationSchema, getWebPageSchema, getBreadcrumbSchema } from '../../utils/seo';
import SiteFooter from '../../components/feature/SiteFooter';
import SiteNav from '../../components/feature/SiteNav';
import PageHero, { gradientTextStyle } from '../../components/feature/PageHero';
import { trackPhoneConversion } from '../../utils/analytics';

const GRADIENT = 'linear-gradient(135deg, #14b8a6 0%, #10b981 100%)';

export default function About() {
  const navigate = useNavigate();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://seitenschmied.com';

  useSEO({
    title: 'Über Seitenschmied – Websites aus Kommunikation, Text und Konzeption',
    description: 'Seitenschmied verbindet Websites mit dem, was vielen Auftritten fehlt: Klarheit, Struktur und Wirkung. Seit 2010 im kreativen Marketing. Seit 2025 unter Seitenschmied.',
    keywords: 'über seitenschmied, webdesign würmtal, website konzeption münchen, texter webdesign, kommunikation webdesign',
    canonical: `${siteUrl}/ueber-uns`,
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        getOrganizationSchema(),
        getWebPageSchema(
          'Über Seitenschmied – Websites aus Kommunikation, Text und Konzeption',
          'Seitenschmied verbindet Websites mit dem, was vielen Auftritten fehlt: Klarheit, Struktur, Kommunikation und Wirkung.',
          `${siteUrl}/ueber-uns`
        ),
        getBreadcrumbSchema([
          { name: 'Startseite', url: siteUrl },
          { name: 'Über Seitenschmied', url: `${siteUrl}/ueber-uns` },
        ]),
        {
          '@type': 'Person',
          name: 'Blasio Hertz',
          jobTitle: 'Texter, Konzepter & Gründer von Seitenschmied',
          worksFor: { '@id': `${siteUrl}/#organization` },
          url: `${siteUrl}/ueber-uns`,
          image: 'https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/5c2178292b687da2c792e75dd14142a8.jpeg',
          description: 'Seit 2010 in kreativem Marketing, Text und Konzeption. Seit 2025 Gründer von Seitenschmied – Websites mit Klarheit, Struktur und Wirkung.',
        },
      ],
    },
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToLeistungen = () => {
    navigate('/');
    setTimeout(() => {
      const el = document.getElementById('leistungen');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Inter', sans-serif" }}>

      <SiteNav activePage="about" navId="about-nav" />

      <PageHero
        label="Über Seitenschmied"
        headline={
          <>
            Im Kopf<br />
            entstanden.<br />
            <span style={gradientTextStyle}>Nicht in der Schublade.</span>
          </>
        }
        subtext="Seit 16 Jahren in kreativem Marketing, Text und Konzeption. Seit 2025 bündelt Seitenschmied diese Erfahrung – mit Websites, die klarer kommunizieren, professioneller wirken und einfach überzeugen."
        backgroundImage="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/54c363a45246fa2fdf2d1b8528fee45e.jpeg"
        backgroundPosition="left"
        ctas={[
          { label: 'Jetzt anrufen', icon: 'ri-phone-line', href: 'tel:+491607676763', variant: 'primary' },
          { label: 'Leistungen ansehen', onClick: scrollToLeistungen, variant: 'secondary' },
        ]}
      />

      {/* ── FOUNDER / HALTUNG ── */}
      <section className="ss-section bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            {/* Bild */}
            <div className="relative">
              <div className="relative overflow-hidden" style={{ aspectRatio: '3/4' }}>
                <img
                  src="https://static.readdy.ai/image/5464a8e208be2c466098e318f0dedf87/5c2178292b687da2c792e75dd14142a8.jpeg"
                  alt="Blasio Hertz – Gründer von Seitenschmied"
                  className="w-full h-full object-cover object-top"
                  style={{ borderRadius: '2px' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
              </div>
              <div
                className="absolute -bottom-6 -right-6 text-white px-6 py-4"
                style={{ background: GRADIENT }}
              >
                <p className="text-xs font-bold tracking-widest uppercase mb-0.5">Gründer</p>
                <p className="text-xl font-black" style={{ fontFamily: "'Outfit', sans-serif" }}>Blasio Hertz</p>
              </div>
              <div className="absolute -top-6 -left-6 w-full h-full bg-gray-100 -z-10" style={{ borderRadius: '2px' }}></div>
            </div>

            {/* Text */}
            <div>
              <p className="ss-label" style={{ color: '#14b8a6' }}>
                Der Kopf dahinter
              </p>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] mb-6 sm:mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Texter.<br />
                Konzepter.<br />
                Kommunikations&shy;mensch.
              </h2>
              <div className="space-y-5 text-gray-600 ss-body">
                <p>
                  Mein Name ist <strong className="text-gray-900">Blasio Hertz</strong>. Ich bin kein Webdesigner aus dem Baukasten-Lager.
                  Ich komme aus der Kommunikation – aus Texten, Konzepten, Kampagnen und Markenkommunikation.
                </p>
                <p>
                  Das verändert, wie ich Websites denke: nicht zuerst visuell, sondern aus der Sicht von
                  <strong className="text-gray-900"> Botschaft, Zielgruppe, Vertrauen und Wirkung</strong>.
                </p>
                <p>
                  Genau dieser Blick fehlt den meisten Websites. Und genau deshalb gibt es Seitenschmied.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gradient divider */}
      <div className="h-px mx-auto max-w-7xl" style={{ background: 'linear-gradient(90deg, transparent, #10b98130, transparent)' }} />

      {/* ── WARUM SEITENSCHMIED ── */}
      <section className="ss-section-dark bg-[#0e0e0e] text-white overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-5xl">
            <p className="ss-label" style={{ color: '#14b8a6' }}>
              Warum Seitenschmied
            </p>
            <h2 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[1.05] mb-10 sm:mb-16" style={{ fontFamily: "'Outfit', sans-serif" }}>
              <strong>Viele Websites zeigen etwas.</strong><br />
              <span style={{
                background: GRADIENT,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}>
                Aber überzeugen zu wenig.
              </span>
            </h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div className="space-y-6 text-gray-300 ss-body leading-relaxed">
              <p>
                Ich habe das in vielen Jahren Kommunikationsarbeit immer wieder erlebt:
                Unternehmen investieren in eine Website, die technisch funktioniert –
                aber nichts auslöst. Kein Vertrauen. Keine Klarheit. Keine Anfragen.
              </p>
              <p>
                Der Grund ist manchmal das Design. Und fast immer: die Botschaft.
                Was steht da eigentlich? Für wen? Warum gerade hier?
              </p>
              <p>
                Seitenschmied ist daraus entstanden, Websites anders zu bauen:
                <strong className="text-white"> klarer, strategischer und mit echtem Kommunikations&shy;verstand.</strong>
              </p>
            </div>

            <div className="border-l-2 pl-6 sm:pl-10" style={{ borderColor: '#14b8a6' }}>
              <p className="text-2xl sm:text-3xl lg:text-4xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                &bdquo;Eine Website muss nicht nur modern aussehen. Sie muss verständlich sagen, warum man hier richtig ist.&ldquo;
              </p>
              <p className="text-sm font-semibold tracking-widest uppercase" style={{ color: '#14b8a6' }}>Blasio Hertz, Seitenschmied</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── WAS SEITENSCHMIED ANDERS MACHT ── */}
      <section className="ss-section bg-white">
        <div className="max-w-7xl mx-auto">
          <p className="ss-label" style={{ color: '#14b8a6' }}>
            Was Seitenschmied anders macht
          </p>
          <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] mb-12 sm:mb-24 max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Vier Dinge,{' '}
            <span style={{
              background: GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}>
              die den
            </span>
            <br />Unterschied machen.
          </h2>

          <div className="space-y-0">
            {[
              {
                n: '01',
                title: 'Kommunikation vor Dekoration.',
                desc: 'Nicht nur hübsch. Sondern klar. Und einfach gut. Eine Website muss nicht nur modern aussehen – sie muss verständlich sagen, warum man hier richtig ist.',
              },
              {
                n: '02',
                title: 'Text als Teil der Wirkung.',
                desc: 'Viele Websites scheitern nicht am Design, sondern an schwachen Botschaften. Hier werden Texte nicht eingefüllt – sie werden konzipiert.',
              },
              {
                n: '03',
                title: 'Klarer Aufbau, der sicher führt.',
                desc: 'Was Besucher zuerst sehen, verstehen und klicken – ist kein Zufall. Jede Seite folgt einer klaren Absicht, nicht einem Template.',
              },
              {
                n: '04',
                title: 'Professioneller Auftritt mit Markenbild.',
                desc: 'Professionell, klar und passend zur Marke – ohne Werbefloskeln und Baukasten-Sprache. Weil gute Kommunikation nicht nach Muster klingt.',
              },
            ].map((usp, idx) => (
              <div key={idx} className="grid lg:grid-cols-[1fr_2fr] gap-6 lg:gap-16 py-10 sm:py-16 border-t border-gray-100 group">
                <div>
                  <span
                    className="ss-deco-number"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      background: GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                      opacity: 0.25,
                    }}
                  >
                    {usp.n}
                  </span>
                </div>
                <div className="grid sm:grid-cols-2 gap-6 lg:gap-8 items-start">
                  <h3 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 leading-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {usp.title}
                  </h3>
                  <p className="text-gray-500 ss-body leading-relaxed sm:pt-2">{usp.desc}</p>
                </div>
              </div>
            ))}
            <div className="border-t border-gray-100" />
          </div>
        </div>
      </section>

      {/* ── ERFAHRUNG / HINTERGRUND ── */}
      <section className="ss-section" style={{ background: 'linear-gradient(135deg, #f8fafb 0%, #f0f9f7 100%)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-24 items-start">
            <div>
              <p className="ss-label" style={{ color: '#14b8a6' }}>
                Erfahrung &amp; Hintergrund
              </p>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black text-gray-900 leading-[1.05] mb-8 sm:mb-10" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Die Grundlage<br />für alles reicht<br />
                <span style={{
                  background: GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  zurück bis 2010.
                </span>
              </h2>
              <div className="space-y-5 text-gray-600 ss-body leading-relaxed">
                <p>
                  Seit 2010 arbeite ich in kreativem Marketing.
                  Für große und kleine Marken. Als Texter, Konzepter und Kommunikationsmensch –
                  in Werbung, Markenkommunikation, Kampagnen, Social Media, Websitetexten und Content.
                </p>
                <p>
                  Seit 2025 bündelt sich diese Erfahrung unter dem Namen Seitenschmied.
                  Mit einem klaren Fokus: <strong className="text-gray-900">Websites, die nicht nur existieren, sondern wirken.</strong>
                </p>
                <p>
                  Die bisherige Arbeit ist unter{' '}
                  <a
                    href="https://blasiohertz.de"
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="font-semibold hover:underline cursor-pointer"
                    style={{ color: '#14b8a6' }}
                  >
                    blasiohertz.de
                  </a>{' '}
                  einsehbar.
                </p>
              </div>
            </div>

            {/* Timeline */}
            <div className="space-y-0 mt-2">
              {[
                { year: '2010', label: 'Start in kreativem Marketing', desc: 'Einstieg als Texter und Konzepter. Werbung, Kampagnen, Markenkommunikation.' },
                { year: '2012–2020', label: 'Für große und kleine Marken', desc: 'Breite Erfahrung in Werbung, Markenkommunikation, Social Media, Content und Websitetexten.' },
                { year: '2020–2024', label: 'Vertiefung auf digitale Kommunikation', desc: 'Fokus auf Websites und digitale Auftritte – Text, Struktur, Botschaft.' },
                { year: '2025', label: 'Gründung von Seitenschmied', desc: 'Alle Erfahrung in einem Angebot. Websites mit Klarheit, Struktur und Wirkung.' },
              ].map((item, idx) => (
                <div key={idx} className="flex gap-6 sm:gap-8 pb-8 sm:pb-10 relative">
                  <div className="flex flex-col items-center">
                    <div className="w-3 h-3 rounded-full mt-1.5 flex-shrink-0" style={{ background: GRADIENT }}></div>
                    {idx < 3 && <div className="w-px flex-1 bg-gray-200 mt-2"></div>}
                  </div>
                  <div className="pb-2">
                    <span className="text-xs font-bold tracking-widest uppercase" style={{ color: '#14b8a6' }}>{item.year}</span>
                    <h4 className="text-lg sm:text-xl font-black text-gray-900 mt-1 mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.label}</h4>
                    <p className="text-gray-500 text-sm sm:text-base leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── WANN WIR SPRECHEN SOLLTEN ── */}
      <section className="ss-section-dark bg-[#0e0e0e] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">
            <div>
              <p className="ss-label" style={{ color: '#14b8a6' }}>
                Entscheidungshilfe
              </p>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl font-black leading-[1.05]" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Wann wir<br />sprechen<br />
                <span style={{
                  background: GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  sollten.
                </span>
              </h2>
            </div>
            <div className="space-y-0">
              {[
                'Wenn Sie gar keine Website haben.',
                'Wenn Sie seit 2 Jahren nichts mehr auf Ihrer Website verändert haben.',
                'Wenn Sie mehr Anfragen möchten.',
                'Wenn Sie gefunden werden wollen.',
                'Wenn Sie Geld für Werbung in Anzeigenblättern verbrennen.',
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-4 sm:gap-6 py-4 sm:py-5 border-b border-white/10">
                  <span
                    className="font-black text-base sm:text-lg mt-0.5 flex-shrink-0"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      background: GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                  <p className="text-gray-200 text-base sm:text-xl font-medium leading-snug">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── PROOF / VERTRAUEN ── */}
      <section className="ss-section-sm bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap items-center gap-8 sm:gap-12 justify-between">
            {[
              { value: '15+', sub1: 'Jahre Erfahrung', sub2: 'in kreativem Marketing' },
              { value: '2010', sub1: 'Start in', sub2: 'Text & Konzeption' },
              { value: '2025', sub1: 'Fokus auf', sub2: 'Websites mit Wirkung' },
            ].map((stat, idx) => (
              <>
                {idx > 0 && <div key={`d-${idx}`} className="w-px h-12 bg-gray-200 hidden lg:block"></div>}
                <div key={idx} className="flex items-center gap-3 sm:gap-4">
                  <span
                    className="text-4xl sm:text-6xl font-black"
                    style={{
                      fontFamily: "'Outfit', sans-serif",
                      background: GRADIENT,
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text',
                    }}
                  >
                    {stat.value}
                  </span>
                  <div>
                    <p className="text-sm text-gray-400 font-medium">{stat.sub1}</p>
                    <p className="text-sm text-gray-600 font-semibold">{stat.sub2}</p>
                  </div>
                </div>
              </>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section className="ss-section bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-end">
            <div>
              <h2 className="text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-black text-gray-900 leading-[1.05] mb-6 sm:mb-8" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Lassen Sie uns Ihre Website&nbsp;<br />
                <span style={{
                  background: GRADIENT,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}>
                  schmieden.
                </span>
              </h2>
              <a href="tel:+491607676763" onClick={(e) => { e.preventDefault(); trackPhoneConversion('tel:+491607676763'); }}>
                <button
                  className="flex items-center gap-2 px-8 sm:px-10 py-4 sm:py-5 font-bold text-base sm:text-lg rounded-xl text-white hover:scale-105 hover:shadow-2xl transition-all duration-300 whitespace-nowrap cursor-pointer"
                  style={{ background: GRADIENT, boxShadow: '0 10px 40px rgba(16,185,129,0.25)' }}
                >
                  <i className="ri-phone-line text-lg"></i>
                  Jetzt anrufen
                </button>
              </a>
            </div>

            <div className="bg-[#0e0e0e] ss-card-pad text-white">
              <p className="text-sm font-bold tracking-[0.2em] uppercase mb-6 sm:mb-8" style={{ color: '#14b8a6' }}>
                Was Sie erwartet
              </p>
              <div className="space-y-5 sm:space-y-6">
                {[
                  { icon: 'ri-chat-3-line', text: 'Kostenloses Erstgespräch – unverbindlich, konkret, auf den Punkt.' },
                  { icon: 'ri-draft-line', text: 'Klares Konzept mit Struktur, Botschaft und Design-Richtung.' },
                  { icon: 'ri-rocket-2-line', text: 'Umsetzung mit echtem Kommunikationsverstand.' },
                  { icon: 'ri-customer-service-2-line', text: 'Betreuung, die bleibt – kein einmaliger Auftrag, sondern Partnerschaft.' },
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-4">
                    <div
                      className="w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: GRADIENT }}
                    >
                      <i className={`${item.icon} text-sm text-white`}></i>
                    </div>
                    <p className="text-gray-300 text-sm sm:text-base leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
